import type { FileKind } from '@felix/domain';
import { ActionIcon, Modal } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconCloudDownload } from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { FileViewer, type FileViewerHandle } from '../documents/FileViewer.js';
import classes from './Compliance.module.css';
import { useCertificateErrorText } from './errors.js';
import type { CertificatePrinter } from './source.js';
import type { CertificatePage } from './types.js';

/** Sayfanın önizleme türü (içerik imzası sunucuda denetlendi; ADR 0029 D12). */
export function pageKind(page: Pick<CertificatePage, 'mimeType' | 'fileName'>): FileKind {
  if (page.mimeType === 'image/png' || /\.png$/i.test(page.fileName)) return 'png';
  if (page.mimeType === 'image/jpeg' || /\.jpe?g$/i.test(page.fileName)) return 'jpg';
  return 'pdf';
}

/** Basılı kopya altlığı (NR496 [4.1.2]): sertifika, yazılım, basım anı (UTC), kişi ve yer. */
export function printFooter(
  t: (key: string, values: Record<string, unknown>) => string,
  certificate: { name: string; number: string },
  printer: CertificatePrinter,
  at: Date,
): string {
  return t('vesselCertificates.viewer.printFooter', {
    name: certificate.name,
    number: certificate.number,
    at: at.toISOString().slice(0, 16).replace('T', ' '),
    person: printer.name,
    place: printer.place,
  });
}

export interface PageViewerProps {
  opened: boolean;
  certificate: { name: string; number: string } | null;
  pages: readonly CertificatePage[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
  pageUrl: (pageId: string) => string;
  onDownload: (page: CertificatePage) => void;
  printer: CertificatePrinter;
  print: () => Promise<void>;
  now?: () => Date;
}

/**
 * Taranmış sayfaların önizleyicisi (plan M7 Task 27a; M8a3 `FileViewer`): sayfalar sırayla, önceki
 * ve sonraki; indirilir ve altlıklı basılır. Gemide inmemiş sayfa "Not on board" (K34).
 */
export function PageViewer({
  opened,
  certificate,
  pages,
  index,
  onIndex,
  onClose,
  pageUrl,
  onDownload,
  printer,
  print,
  now = () => new Date(),
}: PageViewerProps) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`vesselCertificates.viewer.${key}`, values);
  const errorText = useCertificateErrorText();
  const viewer = useRef<FileViewerHandle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const sorted = [...pages].sort((a, b) => a.sortOrder - b.sortOrder);
  const current = sorted[Math.min(index, sorted.length - 1)] ?? null;
  const missing = current?.onBoard !== undefined && current.onBoard !== 'on_board';

  const doPrint = async () => {
    if (!viewer.current || !certificate) return;
    setError(null);
    try {
      const cleanup = await viewer.current.preparePrint(
        printFooter(t, certificate, printer, now()),
      );
      try {
        await print();
      } finally {
        cleanup();
      }
    } catch (failure) {
      setError(failure);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="min(1100px, 96vw)"
      title={
        <span className={classes.drawerTitle}>
          {certificate && current
            ? k('title', {
                name: certificate.name,
                page: sorted.indexOf(current) + 1,
                count: sorted.length,
              })
            : ''}
        </span>
      }
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <div className={classes.viewer}>
        <div className={classes.viewerNav}>
          <ActionIcon
            variant="default"
            aria-label={k('previous')}
            disabled={index <= 0}
            onClick={() => onIndex(index - 1)}
          >
            <IconChevronLeft size={16} />
          </ActionIcon>
          <span className={classes.muted}>{current?.fileName ?? ''}</span>
          <ActionIcon
            variant="default"
            aria-label={k('next')}
            disabled={index >= sorted.length - 1}
            onClick={() => onIndex(index + 1)}
          >
            <IconChevronRight size={16} />
          </ActionIcon>
        </div>
        {error ? (
          <p className={classes.formError} role="alert">
            {errorText(error)}
          </p>
        ) : null}
        {current === null ? (
          <EmptyState title={t('vesselCertificates.details.pages.empty')} />
        ) : missing ? (
          <EmptyState
            icon={<IconCloudDownload size={22} />}
            title={t(`vesselCertificates.details.pages.onBoard.${current.onBoard}`)}
            description={k('notOnBoard')}
          />
        ) : (
          <FileViewer
            ref={viewer}
            key={current.id}
            file={{
              id: current.id,
              name: current.fileName,
              size: current.size,
              kind: pageKind(current),
            }}
            url={pageUrl(current.id)}
            state="published"
            canDownload
            canPrint
            onDownload={() => onDownload(current)}
            onPrint={() => void doPrint()}
          />
        )}
      </div>
    </Modal>
  );
}
